import { useCallback, useEffect, useState } from 'react';
import { getAuthHeaders } from '../services/api';

export const VIE_SCOLAIRE_EVENT = 'vieScolaireUpdated';

export const triggerVieScolaireUpdate = () => {
  window.dispatchEvent(new CustomEvent(VIE_SCOLAIRE_EVENT));
};

export const TYPE_OPTIONS = [
  { value: 'absence', label: 'Absence' },
  { value: 'retard', label: 'Retard' },
  { value: 'incident', label: 'Incident' },
  { value: 'sanction', label: 'Sanction' },
  { value: 'degradation', label: 'Dégradation' },
  { value: 'convocation', label: 'Convocation' },
  { value: 'entretien', label: 'Entretien' },
  { value: 'observation_positive', label: 'Observation positive' },
  { value: 'note_interne', label: 'Note interne' },
  { value: 'autre', label: 'Autre' },
];

export const GRAVITE_OPTIONS = [
  { value: 'info', label: 'Info' },
  { value: 'leger', label: 'Léger' },
  { value: 'moyen', label: 'Moyen' },
  { value: 'grave', label: 'Grave' },
  { value: 'critique', label: 'Critique' },
];

export const STATUT_OPTIONS = [
  { value: 'nouveau', label: 'Nouveau' },
  { value: 'en_cours', label: 'En cours' },
  { value: 'traite', label: 'Traité' },
  { value: 'clos', label: 'Clos' },
  { value: 'archive', label: 'Archivé' },
];

const parseJsonResponse = async (response) => {
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.message || `Erreur ${response.status}`);
  }

  return data;
};

const parseFileResponse = async (response) => {
  if (response.ok) return response;

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || `Erreur ${response.status}`);
  }

  const text = await response.text().catch(() => '');
  throw new Error(text || `Erreur ${response.status}`);
};

const getJsonHeaders = () => getAuthHeaders();

const getFormDataHeaders = () => {
  const headers = getAuthHeaders();
  delete headers['Content-Type'];
  return headers;
};

const cleanFilters = (filters) => {
  const params = new URLSearchParams();

  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, value);
    }
  });

  return params.toString();
};

const appendPayloadToFormData = (formData, payload) => {
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === 'boolean') {
      formData.append(key, value ? '1' : '0');
      return;
    }
    formData.append(key, value);
  });
};

const getFilenameFromDisposition = (disposition, fallback) => {
  if (!disposition) return fallback;

  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  const classicMatch = disposition.match(/filename="?([^"]+)"?/i);
  const rawFilename = utf8Match?.[1] || classicMatch?.[1];

  if (!rawFilename) return fallback;

  try {
    return decodeURIComponent(rawFilename);
  } catch {
    return rawFilename;
  }
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const useVieScolaire = () => {
  const [dashboard, setDashboard] = useState({
    stats: {},
    byType: [],
    watchList: [],
    recents: [],
  });
  const [evenements, setEvenements] = useState([]);
  const [eleves, setEleves] = useState([]);
  const [classes, setClasses] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 30 });
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [loadingEvenements, setLoadingEvenements] = useState(true);
  const [loadingEleves, setLoadingEleves] = useState(true);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [error, setError] = useState(null);

  const loadDashboard = useCallback(async () => {
    try {
      setLoadingDashboard(true);

      const response = await fetch('/api/vie-scolaire/dashboard', {
        method: 'GET',
        headers: getJsonHeaders(),
        credentials: 'include',
      });

      const data = await parseJsonResponse(response);
      setDashboard(data.data || { stats: {}, byType: [], watchList: [], recents: [] });
      setError(null);
      return data.data;
    } catch (err) {
      setError(err.message);
      setDashboard({ stats: {}, byType: [], watchList: [], recents: [] });
      return null;
    } finally {
      setLoadingDashboard(false);
    }
  }, []);

  const loadEvenements = useCallback(async (filters = {}) => {
    try {
      setLoadingEvenements(true);
      const query = cleanFilters(filters);
      const url = `/api/vie-scolaire/evenements${query ? `?${query}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getJsonHeaders(),
        credentials: 'include',
      });

      const data = await parseJsonResponse(response);
      const payload = data.data || {};

      setEvenements(payload.items || []);
      setPagination({
        total: payload.total || 0,
        page: payload.page || 1,
        limit: payload.limit || 30,
      });
      setError(null);
      return payload;
    } catch (err) {
      setError(err.message);
      setEvenements([]);
      return null;
    } finally {
      setLoadingEvenements(false);
    }
  }, []);

  const loadEleves = useCallback(async (filters = {}) => {
    try {
      setLoadingEleves(true);
      const query = cleanFilters(filters);
      const url = `/api/vie-scolaire/eleves${query ? `?${query}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getJsonHeaders(),
        credentials: 'include',
      });

      const data = await parseJsonResponse(response);
      setEleves(data.data || []);
      setError(null);
      return data.data || [];
    } catch (err) {
      setError(err.message);
      setEleves([]);
      return [];
    } finally {
      setLoadingEleves(false);
    }
  }, []);

  const loadClasses = useCallback(async () => {
    try {
      setLoadingClasses(true);

      const response = await fetch('/api/vie-scolaire/classes', {
        method: 'GET',
        headers: getJsonHeaders(),
        credentials: 'include',
      });

      const data = await parseJsonResponse(response);
      setClasses(data.data || []);
      setError(null);
      return data.data || [];
    } catch (err) {
      setError(err.message);
      setClasses([]);
      return [];
    } finally {
      setLoadingClasses(false);
    }
  }, []);

  const getEvenement = async (id) => {
    const response = await fetch(`/api/vie-scolaire/evenements/${id}`, {
      method: 'GET',
      headers: getJsonHeaders(),
      credentials: 'include',
    });

    const data = await parseJsonResponse(response);
    return data.data;
  };

  const createEvenement = async (payload, files = []) => {
    try {
      const formData = new FormData();
      appendPayloadToFormData(formData, payload);
      files.forEach((file) => formData.append('pieces_jointes', file));

      const response = await fetch('/api/vie-scolaire/evenements', {
        method: 'POST',
        headers: getFormDataHeaders(),
        credentials: 'include',
        body: formData,
      });

      const data = await parseJsonResponse(response);
      triggerVieScolaireUpdate();
      return { success: true, data: data.data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const updateEvenement = async (id, payload) => {
    try {
      const response = await fetch(`/api/vie-scolaire/evenements/${id}`, {
        method: 'PUT',
        headers: getJsonHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await parseJsonResponse(response);
      triggerVieScolaireUpdate();
      return { success: true, data: data.data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const deleteEvenement = async (id) => {
    try {
      const response = await fetch(`/api/vie-scolaire/evenements/${id}`, {
        method: 'DELETE',
        headers: getJsonHeaders(),
        credentials: 'include',
      });

      await parseJsonResponse(response);
      triggerVieScolaireUpdate();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const addNoteEvenement = async (id, commentaire) => {
    try {
      const response = await fetch(`/api/vie-scolaire/evenements/${id}/notes`, {
        method: 'POST',
        headers: getJsonHeaders(),
        credentials: 'include',
        body: JSON.stringify({ commentaire }),
      });

      const data = await parseJsonResponse(response);
      triggerVieScolaireUpdate();
      return { success: true, data: data.data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const closeEvenement = async (id, solution_cloture) => {
    try {
      const response = await fetch(`/api/vie-scolaire/evenements/${id}/cloturer`, {
        method: 'POST',
        headers: getJsonHeaders(),
        credentials: 'include',
        body: JSON.stringify({ solution_cloture }),
      });

      const data = await parseJsonResponse(response);
      triggerVieScolaireUpdate();
      return { success: true, data: data.data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const addPiecesJointes = async (id, files = []) => {
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('pieces_jointes', file));

      const response = await fetch(`/api/vie-scolaire/evenements/${id}/pieces-jointes`, {
        method: 'POST',
        headers: getFormDataHeaders(),
        credentials: 'include',
        body: formData,
      });

      const data = await parseJsonResponse(response);
      triggerVieScolaireUpdate();
      return { success: true, data: data.data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const fetchPieceJointeBlob = async (id, mode = 'view') => {
    const response = await fetch(`/api/vie-scolaire/pieces-jointes/${id}/${mode}`, {
      method: 'GET',
      headers: getJsonHeaders(),
      credentials: 'include',
    });

    const fileResponse = await parseFileResponse(response);
    const blob = await fileResponse.blob();
    const filename = getFilenameFromDisposition(
      fileResponse.headers.get('content-disposition'),
      'piece-jointe'
    );

    return {
      blob,
      filename,
      contentType: fileResponse.headers.get('content-type') || blob.type,
    };
  };

  const downloadPieceJointe = async (id, fallbackName = 'piece-jointe') => {
    const { blob, filename } = await fetchPieceJointeBlob(id, 'download');
    downloadBlob(blob, filename || fallbackName);
  };

  useEffect(() => {
    loadDashboard();
    loadEvenements();
    loadEleves();
    loadClasses();

    const handleUpdate = () => {
      loadDashboard();
      loadEvenements();
      loadEleves();
    };

    window.addEventListener(VIE_SCOLAIRE_EVENT, handleUpdate);
    return () => window.removeEventListener(VIE_SCOLAIRE_EVENT, handleUpdate);
  }, [loadClasses, loadDashboard, loadEleves, loadEvenements]);

  return {
    dashboard,
    evenements,
    eleves,
    classes,
    pagination,
    loadingDashboard,
    loadingEvenements,
    loadingEleves,
    loadingClasses,
    error,
    loadDashboard,
    loadEvenements,
    loadEleves,
    loadClasses,
    getEvenement,
    createEvenement,
    updateEvenement,
    deleteEvenement,
    addNoteEvenement,
    closeEvenement,
    addPiecesJointes,
    fetchPieceJointeBlob,
    downloadPieceJointe,
  };
};

export default useVieScolaire;
